import { SkpTransferFile } from "../../entities";

/**
 * The prominent download call-to-action offered on the transfer detail page.
 *
 * - `none`: nothing to offer, because the transfer is empty or its only file is
 *   not downloadable.
 * - `zip`: the transfer holds several files, offer them as one ZIP.
 * - `singleFile`: the transfer holds exactly one file, offer that file as-is.
 */
export type TransferDownloadCta =
  | { kind: "none" }
  | { kind: "zip" }
  | { kind: "singleFile"; file: SkpTransferFile };

/**
 * Whether the given file may be downloaded.
 *
 * The backend `quarantined` flag decides this on its own: a file may carry a
 * non-OK malware status and still be downloadable, because the admin can choose
 * not to quarantine that status. Those files are only marked visually.
 */
function isDownloadable(file: SkpTransferFile): boolean {
  return file.quarantined !== true;
}

/**
 * Resolves the call-to-action the transfer detail page may offer for the given
 * files.
 *
 * A one-file transfer offers that file directly instead of a ZIP: zipping a lone
 * file would only leave the recipient with two files and an unpacking step.
 */
export function resolveTransferDownloadCta(
  files: readonly SkpTransferFile[] | undefined
): TransferDownloadCta {
  if (!files || files.length === 0) return { kind: "none" };
  if (files.length > 1) return { kind: "zip" };

  const file = files[0];

  return isDownloadable(file) ? { kind: "singleFile", file } : { kind: "none" };
}
